import React, { useState } from 'react';
import { Modal, Card, Descriptions, Input } from 'antd';
import { toast } from 'react-toastify';

import { DeviceType, DeviceTypeRelation } from '@services/types';
import api from '@services/api';
import { AppContext } from '@layouts/AppRouter';
import { Navigation } from '@constants/Navigation';
import { useHistory } from 'react-router-dom';

interface Props {
  setDeviceTypes: (deviceTypes: DeviceType[]) => void;
  deviceTypes: DeviceType[];
  deviceTypeToDelete: DeviceType;
  visible: boolean;
  onClose: VoidFunction;
}

const DeleteDeviceTypeModal: React.FC<Props> = ({
  setDeviceTypes,
  deviceTypes,
  deviceTypeToDelete,
  visible,
  onClose,
}) => {
  const { setDeviceFilter } = React.useContext(AppContext);
  const [relation, setRelation] = useState<DeviceTypeRelation>();
  const [loading, setLoading] = useState(true);
  const [additionalData, setAdditionalData] = useState('');
  const history = useHistory();

  React.useEffect(() => {
    api.administration.getDeviceTypeRelation(deviceTypeToDelete?.id).then((data) => {
      setRelation(data);
      setLoading(false);
    });
  }, []);

  const handleSubmit = async () => {
    if (additionalData === deviceTypeToDelete.deviceName) {
      try {
        await api.administration.deleteDeviceType(deviceTypeToDelete.id);
        setDeviceTypes(deviceTypes.filter(({ id }: DeviceType) => id !== deviceTypeToDelete?.id));
        toast.success('Device Type was deleted successfully');
        onClose();
      } catch (error) {
        console.log('Device Type was not deleted ~ error', error);
        toast.error('Device Type was not deleted');
      }
    } else {
      toast.error('Device Type name is incorrect');
    }
  };

  return (
    <Modal
      width="600px"
      visible={visible}
      title={`Delete ${deviceTypeToDelete.deviceName}`}
      okText={'Delete'}
      cancelText="Cancel"
      onCancel={onClose}
      onOk={handleSubmit}
    >
      <Card title="Dependencies for this device type" bodyStyle={{ padding: 0 }} loading={loading}>
        <Descriptions labelStyle={{ fontWeight: '500', fontSize: '16px' }} layout="horizontal" column={1} bordered>
          {relation?.devices?.count && relation?.devices?.count >= 1 && (
            <Descriptions.Item label="Devices">
              <span
                style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => {
                  setDeviceFilter(deviceTypeToDelete?.id);
                  history.push(Navigation.Devices);
                }}
              >
                {relation?.devices?.count}
              </span>
            </Descriptions.Item>
          )}
          {relation?.provisioningDevices?.count && relation?.provisioningDevices?.count >= 1 && (
            <Descriptions.Item label="Provisioning Devices">
              <span
                style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => {
                  setDeviceFilter(deviceTypeToDelete?.id);
                  history.push(Navigation.ProvisionedDevices);
                }}
              >
                {relation?.provisioningDevices?.count}
              </span>
            </Descriptions.Item>
          )}
          {relation?.stbGroups?.count && relation?.stbGroups?.count >= 1 && (
            <Descriptions.Item label="Stb Groups">
              <span
                style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => {
                  setDeviceFilter(deviceTypeToDelete?.id);
                  history.push(Navigation.STBGroup);
                }}
              >
                {relation?.stbGroups?.count}
              </span>
            </Descriptions.Item>
          )}
          {relation?.upgradeRules?.count && relation?.upgradeRules?.count >= 1 && (
            <Descriptions.Item label="Upgrade Rules">
              <span
                style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => {
                  setDeviceFilter(deviceTypeToDelete?.id);
                  history.push(Navigation.Rules);
                }}
              >
                {relation?.upgradeRules?.count}
              </span>
            </Descriptions.Item>
          )}
          {relation?.firmwares?.count && relation?.firmwares?.count >= 1 && (
            <Descriptions.Item label="Firmwares">
              <span
                style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => {
                  setDeviceFilter(deviceTypeToDelete?.id);
                  history.push(Navigation.Firmwares);
                }}
              >
                {relation?.firmwares?.count}
              </span>
            </Descriptions.Item>
          )}
          {relation?.rescue?.packageName?.length && (
            <Descriptions.Item label="Rescue">{relation?.rescue?.packageName}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <div>
        <p>Please write the Device Type name for deletion:</p>
        <Input
          onChange={(e) => {
            setAdditionalData(e.target.value);
          }}
        />
      </div>
    </Modal>
  );
};

export default React.memo(DeleteDeviceTypeModal);
